import { useLayoutEffect, useRef, useState } from 'react';
import { foundation } from '../../../ui';
import MfaOtpInputs, { OTP_CELL_MIN_PX, otpGapPx } from '../MfaOtpInputs';
import { AUTH_CONTROL_HEIGHT } from '../loginAuthTheme';
import { MFA_SIDEWAYS_CELL_PX } from './mfaStyles';

const f = foundation;
const INPUT_H_PX = parseFloat(AUTH_CONTROL_HEIGHT);
// Room for the focused cell's 2px ring so the scroll region never clips it.
const RING_ROOM_PX = 3;

const wrapStyle = {
  width: '100%',
  padding: `${RING_ROOM_PX}px`,
  boxSizing: 'border-box',
};

/**
 * Size-only fit: 1 while six sign-in cells (36px floor) + gaps fit the
 * content box; below that, the ratio that makes them fit exactly.
 */
export function otpFitScale(contentW, viewportW) {
  if (!(contentW > 0)) return 1;
  const need = 6 * OTP_CELL_MIN_PX + 5 * otpGapPx(viewportW);
  return contentW >= need ? 1 : contentW / need;
}

/**
 * RD-SEC-015 — the ONE OTP row every MFA surface renders (stepper code/resume,
 * turn-off, regenerate, change-phone). Renders `MfaOtpInputs` with EXACTLY the
 * props the sign-in 2FA screen passes (`MfaVerifyScreen`: 48px `inputH`, sans),
 * so cell size, fill, radius, font, gap and focus ring match sign-in. The only
 * divergence is a width fallback: when the modal body is too narrow for six
 * sign-in cells (320px phone), everything scales down by one factor — size
 * only, never the look.
 *
 * `sideways` (a pop-up on a phone held sideways, KAN-1012): cells are 44px tall instead of 48 so the
 * row fits the short band; the digit size, width fit, fill and ring are unchanged. Sign-in and the
 * forced-enrollment gates never pass it.
 */
export default function MfaOtpRow({ sideways = false, ...props }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return undefined;
    const measure = () => {
      const next = otpFitScale(el.clientWidth - 2 * RING_ROOM_PX, window.innerWidth);
      setScale((prev) => (Math.abs(prev - next) < 0.005 ? prev : next));
    };
    measure();
    if (typeof window.ResizeObserver !== 'function') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new window.ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fitted = scale < 1;
  const baseH = sideways ? MFA_SIDEWAYS_CELL_PX : INPUT_H_PX;
  const restH = sideways ? `${baseH}px` : AUTH_CONTROL_HEIGHT;
  return (
    <div ref={ref} style={wrapStyle} data-testid="mfa-otp-row" data-otp-fit={fitted ? scale.toFixed(3) : undefined}>
      <MfaOtpInputs
        inputH={fitted ? `${(baseH * scale).toFixed(2)}px` : restH}
        sans={f.fontFamily.sans}
        onFilled={() => {}}
        {...props}
        fitScale={fitted ? scale : undefined}
      />
    </div>
  );
}
